import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useData } from '../contexts/DataContext';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { isPaymentsEnabled, ONBOARDING_V2_ENABLED } from '../config/featureFlags';
import { usePreferences } from '../contexts/PreferencesContext';
import { PaymentsServiceApi } from '../services/PaymentsServiceApi';
import { EndeavorApi } from '../services/api/EndeavorApi';
import { EmailApi } from '../services/ApiService';
import { CORPORATE_EMAIL } from '../config/runtime';
import BaseModal from '../components/modals/BaseModal/BaseModal';
import ThemeSelectorModal from '../components/theme/ThemeSelectorModal';
import { THEME_META } from '../theme/themes';
import './UserProfile.css';

// NOTE: This page already mostly uses tokenized variables (semantic + fallback). Keep new work aligned with newly added Course / Chat / Event components.

export default function UserProfile() {
  const { user, userId, updateUserProfile, changePassword, logout } = useAuth();
  const { subscription, status, refresh: refreshSubscription } = useSubscription();
  const { getEndeavorEvents, clearCache, hydrateDashboard } = useData();
  const { addToast } = useToast();
  const { patchPreferences, preferences } = usePreferences();
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [saving, setSaving] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [msg, setMsg] = useState(null);
  const [pwMsg, setPwMsg] = useState(null);
  // Subscription / plans state
  const [plans, setPlans] = useState([]);
  const [subscriptionMsg, setSubscriptionMsg] = useState(null);
  // Clear data state
  const [showWipeDataModal, setShowWipeDataModal] = useState(false);
  const [wipeConfirmText, setWipeConfirmText] = useState('');
  const [isWiping, setIsWiping] = useState(false);
  const [wipeResultMsg, setWipeResultMsg] = useState(null);
  // Previous semester wipe state
  const [showWipePrevSemesterModal, setShowWipePrevSemesterModal] = useState(false);
  const [prevSemesterCourses, setPrevSemesterCourses] = useState([]);
  const [isLoadingPrevSemester, setIsLoadingPrevSemester] = useState(false);
  const [prevSemesterConfirmText, setPrevSemesterConfirmText] = useState('');
  const [isWipingPrevSemester, setIsWipingPrevSemester] = useState(false);
  const [prevSemesterResultMsg, setPrevSemesterResultMsg] = useState(null);
  // Feedback modal state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackSubject, setFeedbackSubject] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);
  // Theme selector modal
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const { currentTheme } = useTheme();

  // Password strength & match rules (mirrors ResetPasswordPage)
  const pwRules = {
    length: newPw.length >= 8,
    uppercase: /[A-Z]/.test(newPw),
    lowercase: /[a-z]/.test(newPw),
    digit: /[0-9]/.test(newPw),
    special: /[!@#$%^&*()]/.test(newPw),
    match: newPw.length > 0 && newPw === confirmPw,
  };
  const allPwValid = Object.values(pwRules).every(Boolean);

  const canSaveProfile = firstName.trim() || lastName.trim();

  const handleSaveProfile = async () => {
    if (!canSaveProfile) return;
    try {
      setSaving(true);
      setMsg(null);
      if (updateUserProfile) {
        await updateUserProfile({ firstName: firstName.trim(), lastName: lastName.trim() });
      }
      setMsg('Profile saved.');
    } catch (e) {
      setMsg(e.message || 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!allPwValid) {
      if (!pwRules.match) {
        setPwMsg('Passwords do not match.');
      } else {
        setPwMsg('Please satisfy all password requirements.');
      }
      return;
    }
    try {
      setPwLoading(true);
      setPwMsg(null);
      if (changePassword) {
        await changePassword({ currentPassword: currentPw, newPassword: newPw });
      }
      setPwMsg('Password updated.');
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
    } catch (e) {
      setPwMsg(e.message || 'Failed to update password.');
    } finally {
      setPwLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      // Redirect to landing page after logout
      if (window?.location) {
        window.location.href = '/';
      }
    } catch (e) {
      console.error('Logout failed:', e);
    }
  };

  const handleOpenWipeModal = () => {
    setWipeConfirmText('');
    setWipeResultMsg(null);
    setShowWipeDataModal(true);
  };

  const handleCloseWipeModal = () => {
    if (isWiping) return; // Prevent closing during deletion
    setShowWipeDataModal(false);
    setWipeConfirmText('');
  };

  const handleConfirmWipeData = async () => {
    try {
      setIsWiping(true);
      setWipeResultMsg(null);

      // Fetch all courses for the user
      if (!userId) {
        setWipeResultMsg('User ID not found.');
        setIsWiping(false);
        return;
      }

      const response = await EndeavorApi.getEndeavors(userId);
      if (!response.ok) {
        setWipeResultMsg('Failed to fetch courses.');
        setIsWiping(false);
        return;
      }

      const data = await response.json();
      const courses = data.content || data || [];
      
      if (!courses || courses.length === 0) {
        setWipeResultMsg('No courses found to delete.');
        setIsWiping(false);
        return;
      }

      // Track successes and failures
      let successCount = 0;
      let failureCount = 0;
      const failedCourses = [];

      // Delete each course
      for (const course of courses) {
        const courseId = course.id || course.endeavorId;
        try {
          const response = await EndeavorApi.deleteEndeavor(courseId);
          if (response.ok) {
            successCount++;
          } else {
            failureCount++;
            failedCourses.push(course.name || `Course ${courseId}`);
          }
        } catch (error) {
          failureCount++;
          failedCourses.push(course.name || `Course ${courseId}`);
          console.error(`Failed to delete course ${courseId}:`, error);
        }
      }

      // Clear cache and rehydrate dashboard
      try {
        clearCache();
        await hydrateDashboard();
      } catch (cacheError) {
        console.error('Failed to refresh cache:', cacheError);
      }

      // Build result message
      if (failureCount === 0) {
        setWipeResultMsg(`Successfully deleted ${successCount} course${successCount !== 1 ? 's' : ''}.`);
      } else if (successCount === 0) {
        setWipeResultMsg(`Failed to delete all ${failureCount} course${failureCount !== 1 ? 's' : ''}.`);
      } else {
        setWipeResultMsg(`Deleted ${successCount} course${successCount !== 1 ? 's' : ''}, ${failureCount} failed: ${failedCourses.join(', ')}`);
      }

      // Close modal after showing results
      setTimeout(() => {
        setShowWipeDataModal(false);
        setWipeConfirmText('');
      }, 3000);

    } catch (error) {
      console.error('Error wiping data:', error);
      setWipeResultMsg(`Error: ${error.message || 'Failed to delete courses'}`);
    } finally {
      setIsWiping(false);
    }
  };

  const handleOpenWipePrevSemesterModal = async () => {
    setIsLoadingPrevSemester(true);
    setPrevSemesterConfirmText('');
    setPrevSemesterResultMsg(null);
    setShowWipePrevSemesterModal(true);

    try {
      // Fetch all courses
      if (!userId) {
        setPrevSemesterResultMsg('User ID not found.');
        setIsLoadingPrevSemester(false);
        return;
      }

      const response = await EndeavorApi.getEndeavors(userId);
      if (!response.ok) {
        setPrevSemesterResultMsg('Failed to fetch courses.');
        setIsLoadingPrevSemester(false);
        return;
      }

      const data = await response.json();
      const courses = data.content || data || [];
      
      if (!courses || courses.length === 0) {
        setPrevSemesterCourses([]);
        setIsLoadingPrevSemester(false);
        return;
      }

      // Find courses with last event over 2 weeks ago
      const twoWeeksAgo = new Date();
      twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
      
      const oldCourses = [];
      
      for (const course of courses) {
        const courseId = course.id || course.endeavorId;
        try {
          // Fetch events for this course
          const events = await getEndeavorEvents(courseId);
          
          if (!events || events.length === 0) {
            // No events means this is an old/inactive course
            oldCourses.push({
              ...course,
              lastEventDate: null,
              reason: 'No events'
            });
            continue;
          }

          // Find the most recent event
          let mostRecentDate = null;
          for (const event of events) {
            const eventDate = new Date(event.startDatetime || event.startDate || event.dueDate);
            if (!isNaN(eventDate.getTime())) {
              if (!mostRecentDate || eventDate > mostRecentDate) {
                mostRecentDate = eventDate;
              }
            }
          }

          // If most recent event is over 2 weeks old (or no valid dates found)
          if (!mostRecentDate || mostRecentDate < twoWeeksAgo) {
            oldCourses.push({
              ...course,
              lastEventDate: mostRecentDate,
              reason: mostRecentDate ? 'Last event over 2 weeks ago' : 'No valid event dates'
            });
          }
        } catch (error) {
          console.error(`Error checking events for course ${courseId}:`, error);
          // If we can't check events, assume it's old
          oldCourses.push({
            ...course,
            lastEventDate: null,
            reason: 'Unable to check events'
          });
        }
      }

      setPrevSemesterCourses(oldCourses);
    } catch (error) {
      console.error('Error finding old courses:', error);
      setPrevSemesterResultMsg(`Error: ${error.message || 'Failed to find old courses'}`);
    } finally {
      setIsLoadingPrevSemester(false);
    }
  };

  const handleCloseWipePrevSemesterModal = () => {
    if (isWipingPrevSemester) return;
    setShowWipePrevSemesterModal(false);
    setPrevSemesterConfirmText('');
    setPrevSemesterCourses([]);
  };

  const handleConfirmWipePrevSemester = async () => {
    try {
      setIsWipingPrevSemester(true);
      setPrevSemesterResultMsg(null);

      if (prevSemesterCourses.length === 0) {
        setPrevSemesterResultMsg('No old courses to delete.');
        setIsWipingPrevSemester(false);
        return;
      }

      // Track successes and failures
      let successCount = 0;
      let failureCount = 0;
      const failedCourses = [];

      // Delete each old course
      for (const course of prevSemesterCourses) {
        const courseId = course.id || course.endeavorId;
        try {
          const response = await EndeavorApi.deleteEndeavor(courseId);
          if (response.ok) {
            successCount++;
          } else {
            failureCount++;
            failedCourses.push(course.name || `Course ${courseId}`);
          }
        } catch (error) {
          failureCount++;
          failedCourses.push(course.name || `Course ${courseId}`);
          console.error(`Failed to delete course ${courseId}:`, error);
        }
      }

      // Clear cache and rehydrate dashboard
      try {
        clearCache();
        await hydrateDashboard();
      } catch (cacheError) {
        console.error('Failed to refresh cache:', cacheError);
      }

      // Build result message
      if (failureCount === 0) {
        setPrevSemesterResultMsg(`Successfully deleted ${successCount} old course${successCount !== 1 ? 's' : ''}.`);
      } else if (successCount === 0) {
        setPrevSemesterResultMsg(`Failed to delete all ${failureCount} course${failureCount !== 1 ? 's' : ''}.`);
      } else {
        setPrevSemesterResultMsg(`Deleted ${successCount} course${successCount !== 1 ? 's' : ''}, ${failureCount} failed: ${failedCourses.join(', ')}`);
      }

      // Close modal after showing results
      setTimeout(() => {
        setShowWipePrevSemesterModal(false);
        setPrevSemesterConfirmText('');
        setPrevSemesterCourses([]);
      }, 3000);

    } catch (error) {
      console.error('Error wiping previous semester data:', error);
      setPrevSemesterResultMsg(`Error: ${error.message || 'Failed to delete courses'}`);
    } finally {
      setIsWipingPrevSemester(false);
    }
  };

  // Load plans (even when subscription exists) to map to a human readable name
  useEffect(() => {
    let ignore = false;
    const loadPlans = async () => {
      if (!isPaymentsEnabled()) return;
      try {
        const list = await PaymentsServiceApi.listPlans();
        if (!ignore) setPlans(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error('Failed to load plans:', e);
      }
    };
    loadPlans();
    return () => { ignore = true; };
  }, []);

  const handleRefreshSubscription = async () => {
    setSubscriptionMsg('Refreshing subscription...');
    try {
      await refreshSubscription?.();
      setSubscriptionMsg('Subscription refreshed.');
    } catch (e) {
      setSubscriptionMsg('Refresh failed.');
    }
  };

  // Feedback submission handler
  const handleSendFeedback = async () => {
    const trimmedSubject = feedbackSubject.trim();
    const trimmedMessage = feedbackMessage.trim();
    
    if (!trimmedSubject || !trimmedMessage) return;
    
    setIsSendingFeedback(true);
    
    try {
      // Determine environment from URL
      const hostname = window.location.hostname;
      let environment = 'production';
      if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
        environment = 'local';
      } else if (hostname.includes('dev') || hostname.includes('development')) {
        environment = 'development';
      } else if (hostname.includes('staging') || hostname.includes('stage')) {
        environment = 'staging';
      }

      const sourcePage = 'User Profile';
      
      const response = await EmailApi.sendFeedback(
        trimmedSubject, 
        trimmedMessage, 
        sourcePage, 
        environment
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to send feedback');
      }
      
      // Success
      setShowFeedbackModal(false);
      setFeedbackSubject('');
      setFeedbackMessage('');
      addToast({
        message: 'Feedback sent. Thank you.',
        intent: 'success',
        duration: 3000
      });
    } catch (err) {
      console.error('Failed to send feedback:', err);
      addToast({
        message: err.message || 'Could not send feedback. Please try again.',
        intent: 'danger',
        duration: 4000
      });
    } finally {
      setIsSendingFeedback(false);
    }
  };

  const handleOpenFeedbackModal = () => {
    setFeedbackSubject('');
    setFeedbackMessage('');
    setShowFeedbackModal(true);
  };

  const handleCloseFeedbackModal = () => {
    if (isSendingFeedback) return;
    setShowFeedbackModal(false);
    setFeedbackSubject('');
    setFeedbackMessage('');
  };

  // Use subscription from context (already handles dual-fetch with mirror)
  const displaySubscription = subscription;

  // Derive human-readable plan label
  const displayPlanName = (() => {
    if (!displaySubscription) return null;
    const subAlias = displaySubscription.planAlias || displaySubscription.plan_alias || null;
    const subPriceId = displaySubscription.priceId || displaySubscription.items?.[0]?.price?.id || displaySubscription.items?.[0]?.price || null;
    const match = plans.find(pl => {
      const alias = pl.alias || pl.planAlias || pl.id || pl.key;
      const priceId = pl.priceId || pl.id;
      return (subAlias && alias === subAlias) || (subPriceId && (priceId === subPriceId || alias === subPriceId));
    });
    return match?.label || match?.name || match?.alias || subAlias || subPriceId || '—';
  })();

  return (
    <>
    <div className="user-profile-page">
      <div className="settings-header">
        <div className="settings-header__left">
          <span className="settings-header__icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
          </span>
          <div className="settings-header__text">
            <h1 className="settings-header__title">Command Center</h1>
            <p className="settings-header__subtitle">Account, preferences, and system settings</p>
          </div>
        </div>
        <div className="settings-header__right">
          <span className="settings-header__user-email">{user?.email}</span>
          {isPaymentsEnabled() && status && (
            <span className="settings-header__badge">{status === 'active' ? 'Pro' : status}</span>
          )}
        </div>
      </div>

      <div className="settings-columns">
        <div className="settings-column settings-column-left">
          <div className="settings-section-header">
            <span className="settings-section-header__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
            </span>
            <div className="settings-section-header__text">
              <h3 className="settings-section-header__title">Account & Security</h3>
              <p className="settings-section-header__subtitle">Your personal info, login, and session</p>
            </div>
          </div>
          <section className="settings-card settings-card--account" id="account-section">
            <div className="settings-card-header">
              <span className="settings-card-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span>
              <h2 className="up-subtitle">Account</h2>
            </div>
            <p className="settings-card-microcopy">Manage your personal information</p>
          <div className="up-field">
            <label>Email</label>
            <input value={user?.email || ''} disabled />
          </div>
          {user?.firstLoginAt && (
            <div className="up-field">
              <label>Member Since</label>
              <input value={new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(user.firstLoginAt))} disabled />
            </div>
          )}
          <div className="up-field">
            <label>First Name</label>
            <input
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              placeholder="First name"
            />
          </div>
          <div className="up-field">
            <label>Last Name</label>
            <input
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              placeholder="Last name"
            />
          </div>
          <div className="up-actions">
            <button
              className="btn-primary"
              disabled={!canSaveProfile || saving}
              onClick={handleSaveProfile}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
            {msg && <span className="up-status">{msg}</span>}
          </div>
        </section>

        <section className="settings-card settings-card--password" id="password-section">
          <div className="settings-card-header">
            <span className="settings-card-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg></span>
            <h2 className="up-subtitle">Reset Password</h2>
          </div>
          <p className="settings-card-microcopy">Update your login credentials securely</p>
          <div className="up-field">
            <label>Current Password</label>
            <input
              type="password"
              value={currentPw}
              onChange={e => setCurrentPw(e.target.value)}
              placeholder="Current password"
            />
          </div>
          <div className="up-field">
            <label>New Password</label>
            <input
              type="password"
              value={newPw}
              onChange={e => setNewPw(e.target.value)}
              placeholder="New password"
              {...(newPw ? { 'aria-describedby': 'profile-password-rules' } : {})}
            />
          </div>
          <div className="up-field">
            <label>Confirm New Password</label>
            <input
              type="password"
              value={confirmPw}
              onChange={e => setConfirmPw(e.target.value)}
              placeholder="Confirm new password"
            />
          </div>
          {newPw && (
            <div id="profile-password-rules" className="password-rules" aria-live="polite">
              <ul>
                <li className={pwRules.length ? 'passed' : 'failed'}>{pwRules.length ? '✓' : '✗'} At least 8 characters</li>
                <li className={pwRules.uppercase ? 'passed' : 'failed'}>{pwRules.uppercase ? '✓' : '✗'} Contains one capital letter</li>
                <li className={pwRules.lowercase ? 'passed' : 'failed'}>{pwRules.lowercase ? '✓' : '✗'} Contains one lowercase letter</li>
                <li className={pwRules.digit ? 'passed' : 'failed'}>{pwRules.digit ? '✓' : '✗'} Contains one digit (0–9)</li>
                <li className={pwRules.special ? 'passed' : 'failed'}>{pwRules.special ? '✓' : '✗'} Contains one special character (!@#$%^&*())</li>
                <li className={pwRules.match ? 'passed' : 'failed'}>{pwRules.match ? '✓' : '✗'} Passwords match</li>
              </ul>
            </div>
          )}
          <div className="up-actions">
            <button
              className="btn-primary"
              disabled={pwLoading || !currentPw || !newPw || !confirmPw || !allPwValid}
              onClick={handleChangePassword}
            >
              {pwLoading ? 'Updating...' : 'Update Password'}
            </button>
            {pwMsg && <span className="up-status">{pwMsg}</span>}
          </div>
        </section>

        <section className="settings-card settings-card--session" id="session-section" style={{background:'var(--semantic-surface-panel, var(--panel-bg))'}}>
          <div className="settings-card-header">
            <span className="settings-card-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></span>
            <h2 className="up-subtitle">Session</h2>
          </div>
          <p className="settings-card-microcopy">Manage your current session</p>
          
          {/* Logout Section */}
          <div className="session-action-group">
            <p className="up-note section-subtitle">Sign out of this browser. You can log back in anytime.</p>
            <button
              className="btn-danger"
              onClick={handleLogout}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              Log Out
            </button>
          </div>
        </section>

        {/* Danger Zone */}
        <div className="settings-danger-zone">
          <div className="settings-danger-zone__header">
            <span className="settings-danger-zone__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </span>
            <div>
              <h4 className="settings-danger-zone__title">Danger Zone</h4>
              <p className="settings-danger-zone__subtitle">These actions are permanent and cannot be undone</p>
            </div>
          </div>
          <section className="settings-card settings-card--danger" id="danger-section">
            <div className="danger-action-group">
              <h4 className="danger-action-group__label">Wipe Current Course Data</h4>
              <p className="danger-action-group__desc">Permanently delete all your courses, events, assignments, and associated documents.</p>
              <button
                className="btn-danger"
                onClick={handleOpenWipeModal}
              >
                Wipe All Current Data
              </button>
              {wipeResultMsg && (
                <div className="up-status" style={{
                  marginTop: '0.5rem',
                  padding: '0.75rem',
                  background: wipeResultMsg.includes('Failed') || wipeResultMsg.includes('failed') 
                    ? 'var(--intent-danger-bg, #fee)' 
                    : 'var(--intent-success-bg, #efe)',
                  color: wipeResultMsg.includes('Failed') || wipeResultMsg.includes('failed')
                    ? 'var(--intent-danger-text, #c00)'
                    : 'var(--intent-success-text, #060)',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}>
                  {wipeResultMsg}
                </div>
              )}
            </div>

            <div className="danger-action-group" style={{paddingTop: '16px', borderTop: '1px dashed var(--border-subtle)'}}>
              <h4 className="danger-action-group__label">Wipe Previous Semester</h4>
              <p className="danger-action-group__desc">Delete only courses with no events in the last 2 weeks.</p>
              <button
                className="btn-danger"
                onClick={handleOpenWipePrevSemesterModal}
              >
                Wipe Old Courses
              </button>
              {prevSemesterResultMsg && (
                <div className="up-status" style={{
                  marginTop: '0.5rem',
                  padding: '0.75rem',
                  background: prevSemesterResultMsg.includes('Failed') || prevSemesterResultMsg.includes('failed')
                    ? 'var(--intent-danger-bg, #fee)'
                    : 'var(--intent-success-bg, #efe)',
                  color: prevSemesterResultMsg.includes('Failed') || prevSemesterResultMsg.includes('failed')
                    ? 'var(--intent-danger-text, #c00)'
                    : 'var(--intent-success-text, #060)',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}>
                  {prevSemesterResultMsg}
                </div>
              )}
            </div>
          </section>
        </div>
        </div>

        <div className="settings-column settings-column-right">
        <div className="settings-section-header">
          <span className="settings-section-header__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
          </span>
          <div className="settings-section-header__text">
            <h3 className="settings-section-header__title">Preferences & Access</h3>
            <p className="settings-section-header__subtitle">Customize your experience and tools</p>
          </div>
        </div>
        {isPaymentsEnabled() && (
          <section className="settings-card settings-card--billing" id="billing-section">
            <div className="settings-card-header">
              <span className="settings-card-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg></span>
              <h2 className="up-subtitle">Billing & Subscription</h2>
            </div>
            <p className="settings-card-microcopy">Manage your plan and payment details</p>
            <div style={{ display:'grid', gap:'0.75rem' }}>
              <div style={{ fontSize:'.75rem', color:'var(--text-muted)' }}>Status: <strong>{status || 'unknown'}</strong></div>
              {displaySubscription && (
                <div style={{ display:'grid', gap:'0.4rem', padding:'0.75rem', background:'var(--semantic-surface-muted)', border:'1px solid var(--border-subtle)', borderRadius:'8px' }}>
                  <div style={{ fontSize:'.8rem' }}>Plan: <strong>{displayPlanName}</strong></div>
                  {displaySubscription.currentPeriodEnd && (
                    <div style={{ fontSize:'.7rem' }}>Current Period Ends: {new Date((typeof displaySubscription.currentPeriodEnd === 'number') ? displaySubscription.currentPeriodEnd*1000 : displaySubscription.currentPeriodEnd).toLocaleString()}</div>
                  )}
                  {displaySubscription.cancelAtPeriodEnd && <div style={{ fontSize:'.7rem', color:'var(--intent-danger-text)' }}>Cancellation scheduled at period end.</div>}
                  <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                    <button className="btn-primary" type="button" onClick={() => window.location.href='/subscription'}>Manage Subscription</button>
                    <button className="btn-secondary" type="button" onClick={handleRefreshSubscription}>Refresh</button>
                  </div>
                  {subscriptionMsg && <div className="up-status" style={{ fontSize:'.65rem' }}>{subscriptionMsg}</div>}
                </div>
              )}
              {!displaySubscription && (
                <div style={{ display:'grid', gap:'0.75rem', padding:'0.75rem', background:'var(--semantic-surface-muted)', border:'1px solid var(--border-subtle)', borderRadius:'8px' }}>
                  <div style={{ fontSize:'.75rem', color:'var(--text-muted)' }}>No active subscription found.</div>
                  <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                    <button className="btn-primary" type="button" onClick={() => window.location.href='/subscription'}>Manage Subscription</button>
                    <button className="btn-secondary" type="button" onClick={handleRefreshSubscription}>Refresh</button>
                  </div>
                  {subscriptionMsg && <div className="up-status" style={{ fontSize:'.65rem' }}>{subscriptionMsg}</div>}
                </div>
              )}
            </div>
          </section>
        )}

        <section className="settings-card settings-card--theme" id="theme-section">
          <div className="settings-card-header">
            <span className="settings-card-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="13.5" cy="6.5" r="2.5"/><circle cx="19" cy="11.5" r="2.5"/><circle cx="17" cy="18" r="2.5"/><circle cx="8.5" cy="18" r="2.5"/><circle cx="6" cy="11.5" r="2.5"/><path d="M12 2a10 10 0 00-1 19.8c.5.1 1-.2 1-.7v-1.5c0-1 .7-1.6 1.5-1.6H15a3 3 0 003-3c0-.4-.1-.8-.3-1.1"/></svg></span>
            <h2 className="up-subtitle">Theme</h2>
          </div>
          <div className="theme-current-row">
            <div className="theme-current">
              <span className="theme-current__label">Current Theme</span>
              <span className="theme-current__name">{THEME_META[currentTheme]?.displayName || currentTheme}</span>
            </div>
            <button className="btn-feature" type="button" onClick={() => setShowThemeSelector(true)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="13.5" cy="6.5" r="2.5"/><circle cx="19" cy="11.5" r="2.5"/><circle cx="17" cy="18" r="2.5"/><circle cx="8.5" cy="18" r="2.5"/><circle cx="6" cy="11.5" r="2.5"/></svg>
              Choose Theme
            </button>
          </div>
          <p className="settings-card-microcopy">Theme switching is also available from your dashboard header.</p>
          <ThemeSelectorModal open={showThemeSelector} onClose={() => setShowThemeSelector(false)} />
        </section>

        <section className="settings-card settings-card--home" id="default-home-section">
          <div className="settings-card-header">
            <span className="settings-card-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></span>
            <h2 className="up-subtitle">Default Home</h2>
          </div>
          <p className="settings-card-microcopy">Your home screen is set automatically. When you have courses, the Dashboard is your home. Without courses, Study Tools is your home.</p>
        </section>

        <section className="settings-card settings-card--documentation" id="documentation-section">
          <div className="settings-card-header">
            <span className="settings-card-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg></span>
            <h2 className="up-subtitle">Documentation</h2>
          </div>
          <p className="settings-card-microcopy">Learn the ins and outs of DocuHounds</p>
          <p className="up-note section-subtitle">Guides, tutorials, and tips to help you get the most out of the platform.</p>
          <div className="up-actions">
            <button className="btn-ghost" type="button" onClick={()=>window.location.href='/docs'}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>
              Explore Guides & Tutorials
            </button>
          </div>
        </section>

        {ONBOARDING_V2_ENABLED && (
          <section className="settings-card" id="onboarding-section">
            <div className="settings-card-header">
              <span className="settings-card-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg></span>
              <h2 className="up-subtitle">Onboarding</h2>
            </div>
            <p className="settings-card-microcopy">Revisit the setup experience</p>
            <p className="up-note section-subtitle">Restart onboarding to update your setup or re-import a syllabus.</p>
            <div className="up-actions">
              <button
                className="btn-primary"
                type="button"
                onClick={async () => {
                  try {
                    await patchPreferences({ onboarding_v2_completed: false });
                    const uid = user?.userId || user?.id;
                    if (uid) {
                      try { localStorage.removeItem(`dh_onboarding_complete:${uid}`); } catch (e) { /* */ }
                    }
                    addToast('Onboarding will appear next time you visit the dashboard.', 'success');
                  } catch (e) {
                    addToast('Failed to reset onboarding. Please try again.', 'error');
                  }
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>
                Restart Onboarding
              </button>
            </div>
          </section>
        )}

        <section className="settings-card settings-card--feedback" id="feedback-section">
          <div className="settings-card-header">
            <span className="settings-card-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg></span>
            <h2 className="up-subtitle">Help Us Improve</h2>
          </div>
          <p className="settings-card-microcopy">Your voice shapes what we build next</p>
          <p className="up-note section-subtitle">Report bugs, request features, or tell us what's working.</p>
          <div className="up-actions">
            <button className="btn-primary" type="button" onClick={handleOpenFeedbackModal}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
              Send Feedback
            </button>
          </div>
        </section>

        {/* Parent Connections — frontend mock only */}
        <section className="settings-card" id="parent-connections-section">
          <div className="settings-card-header">
            <span className="settings-card-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg></span>
            <h2 className="up-subtitle">Parent Connections</h2>
          </div>
          <p className="up-note section-subtitle">Manage parent or guardian access to your academic progress.</p>
          <div style={{ padding: '0.75rem 0' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.75rem 1rem', background: 'var(--hover-bg, rgba(0,0,0,0.02))',
              borderRadius: '8px', marginBottom: '0.5rem'
            }}>
              <div>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>Sarah Carter</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Parent • Connected</div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn-secondary" style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }} onClick={() => {}}>
                  Remove
                </button>
              </div>
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
              Shared: Assignment status, due dates, study activity summaries, course health, alerts
            </div>
          </div>
        </section>

        </div>
      </div>
    </div>

    {/* Wipe Data Confirmation Modal */}
    <BaseModal
      isOpen={showWipeDataModal}
      onClose={handleCloseWipeModal}
      title="Wipe All Course Data?"
      maxWidth="520px"
      footer={
        <div className="modal-footer-actions" style={{display: 'flex', gap: '0.75rem', justifyContent: 'flex-end'}}>
          <button 
            className="btn-secondary" 
            onClick={handleCloseWipeModal}
            disabled={isWiping}
          >
            Cancel
          </button>
          <button 
            className="btn-danger" 
            onClick={handleConfirmWipeData}
            disabled={isWiping || wipeConfirmText.trim().toLowerCase() !== 'wipe all'}
          >
            {isWiping ? 'Deleting...' : 'Wipe All Data'}
          </button>
        </div>
      }
    >
      {isWiping ? (
        <div style={{textAlign: 'center', padding: '2rem 0'}}>
          <div style={{
            display: 'inline-block',
            width: '40px',
            height: '40px',
            border: '4px solid var(--border-subtle)',
            borderTop: '4px solid var(--intent-danger-text)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '1rem'
          }}></div>
          <p style={{color: 'var(--text-secondary)', fontSize: '0.875rem'}}>
            Deleting all courses and their data...
          </p>
        </div>
      ) : (
        <>
          <div style={{marginBottom: '1.5rem'}}>
            <p style={{lineHeight: '1.6', color: 'var(--text-secondary)', marginBottom: '1rem'}}>
              This will permanently delete <strong>all your courses</strong> and their associated data including:
            </p>
            <ul style={{
              lineHeight: '1.6',
              color: 'var(--text-secondary)',
              paddingLeft: '1.5rem',
              marginBottom: '1rem'
            }}>
              <li>All courses</li>
              <li>All events and calendar items</li>
              <li>All assignments</li>
              <li>All document associations</li>
              <li>All course metadata</li>
            </ul>
            <p style={{
              lineHeight: '1.6',
              color: 'var(--intent-danger-text)',
              fontWeight: '600',
              padding: '0.75rem',
              background: 'var(--intent-danger-bg, #fee)',
              borderRadius: '6px',
              marginTop: '1rem'
            }}>
              ⚠️ This action cannot be undone!
            </p>
          </div>
          
          <div style={{marginBottom: '0.5rem'}}>
            <label htmlFor="wipe-confirm" style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: 'var(--text-primary)'
            }}>
              Type <code style={{
                background: 'var(--semantic-surface-muted)',
                padding: '0.125rem 0.375rem',
                borderRadius: '3px',
                fontFamily: 'monospace'
              }}>wipe all</code> to confirm:
            </label>
            <input
              id="wipe-confirm"
              type="text"
              value={wipeConfirmText}
              onChange={(e) => setWipeConfirmText(e.target.value)}
              placeholder="Type 'wipe all' to confirm"
              autoComplete="off"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--border-subtle)',
                borderRadius: '6px',
                fontSize: '0.875rem',
                background: 'var(--semantic-surface-input, var(--card-bg))',
                color: 'var(--text-primary)'
              }}
            />
          </div>
          
          {wipeResultMsg && (
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: wipeResultMsg.includes('Failed') || wipeResultMsg.includes('failed')
                ? 'var(--intent-danger-bg, #fee)'
                : 'var(--intent-success-bg, #efe)',
              color: wipeResultMsg.includes('Failed') || wipeResultMsg.includes('failed')
                ? 'var(--intent-danger-text, #c00)'
                : 'var(--intent-success-text, #060)',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}>
              {wipeResultMsg}
            </div>
          )}
        </>
      )}
    </BaseModal>

    {/* Wipe Previous Semester Confirmation Modal */}
    <BaseModal
      isOpen={showWipePrevSemesterModal}
      onClose={handleCloseWipePrevSemesterModal}
      title="Wipe Previous Semester Courses?"
      maxWidth="560px"
      footer={
        <div className="modal-footer-actions" style={{display: 'flex', gap: '0.75rem', justifyContent: 'flex-end'}}>
          <button 
            className="btn-secondary" 
            onClick={handleCloseWipePrevSemesterModal}
            disabled={isWipingPrevSemester}
          >
            Cancel
          </button>
          <button 
            className="btn-danger" 
            onClick={handleConfirmWipePrevSemester}
            disabled={isWipingPrevSemester || isLoadingPrevSemester || prevSemesterConfirmText.trim().toLowerCase() !== 'wipe all' || prevSemesterCourses.length === 0}
          >
            {isWipingPrevSemester ? 'Deleting...' : 'Wipe Old Courses'}
          </button>
        </div>
      }
    >
      {isLoadingPrevSemester ? (
        <div style={{textAlign: 'center', padding: '2rem 0'}}>
          <div style={{
            display: 'inline-block',
            width: '40px',
            height: '40px',
            border: '4px solid var(--border-subtle)',
            borderTop: '4px solid var(--color-brand-primary)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '1rem'
          }}></div>
          <p style={{color: 'var(--text-secondary)', fontSize: '0.875rem'}}>
            Analyzing courses and their events...
          </p>
        </div>
      ) : isWipingPrevSemester ? (
        <div style={{textAlign: 'center', padding: '2rem 0'}}>
          <div style={{
            display: 'inline-block',
            width: '40px',
            height: '40px',
            border: '4px solid var(--border-subtle)',
            borderTop: '4px solid var(--intent-danger-text)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '1rem'
          }}></div>
          <p style={{color: 'var(--text-secondary)', fontSize: '0.875rem'}}>
            Deleting {prevSemesterCourses.length} old course{prevSemesterCourses.length !== 1 ? 's' : ''}...
          </p>
        </div>
      ) : (
        <>
          <div style={{marginBottom: '1.5rem'}}>
            <p style={{lineHeight: '1.6', color: 'var(--text-secondary)', marginBottom: '1rem'}}>
              The following <strong>{prevSemesterCourses.length} course{prevSemesterCourses.length !== 1 ? 's' : ''}</strong> will be deleted (courses with no events in the last 2 weeks):
            </p>
            
            {prevSemesterCourses.length === 0 ? (
              <div style={{
                padding: '1rem',
                background: 'var(--semantic-surface-muted)',
                borderRadius: '6px',
                textAlign: 'center',
                color: 'var(--text-secondary)',
                fontSize: '0.875rem'
              }}>
                ✓ No old courses found. All your courses have recent events!
              </div>
            ) : (
              <div style={{
                maxHeight: '300px',
                overflowY: 'auto',
                border: '1px solid var(--border-subtle)',
                borderRadius: '6px',
                background: 'var(--semantic-surface-muted)',
                padding: '0.5rem'
              }}>
                {prevSemesterCourses.map((course, index) => (
                  <div key={index} style={{
                    padding: '0.75rem',
                    marginBottom: '0.5rem',
                    background: 'var(--card-bg)',
                    borderRadius: '4px',
                    border: '1px solid var(--border-subtle)'
                  }}>
                    <div style={{fontWeight: '600', color: 'var(--text-primary)', marginBottom: '0.25rem'}}>
                      {course.name || `Course ${course.id || course.endeavorId}`}
                    </div>
                    <div style={{fontSize: '0.75rem', color: 'var(--text-muted)'}}>
                      {course.lastEventDate ? (
                        <>Last event: {new Date(course.lastEventDate).toLocaleDateString()}</>
                      ) : (
                        <>{course.reason || 'No events'}</>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {prevSemesterCourses.length > 0 && (
              <p style={{
                lineHeight: '1.6',
                color: 'var(--intent-danger-text)',
                fontWeight: '600',
                padding: '0.75rem',
                background: 'var(--intent-danger-bg, #fee)',
                borderRadius: '6px',
                marginTop: '1rem',
                fontSize: '0.875rem'
              }}>
                ⚠️ This action cannot be undone!
              </p>
            )}
          </div>
          
          {prevSemesterCourses.length > 0 && (
            <div style={{marginBottom: '0.5rem'}}>
              <label htmlFor="prev-semester-confirm" style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: 'var(--text-primary)'
              }}>
                Type <code style={{
                  background: 'var(--semantic-surface-muted)',
                  padding: '0.125rem 0.375rem',
                  borderRadius: '3px',
                  fontFamily: 'monospace'
                }}>wipe all</code> to confirm:
              </label>
              <input
                id="prev-semester-confirm"
                type="text"
                value={prevSemesterConfirmText}
                onChange={(e) => setPrevSemesterConfirmText(e.target.value)}
                placeholder="Type 'wipe all' to confirm"
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  background: 'var(--semantic-surface-input, var(--card-bg))',
                  color: 'var(--text-primary)'
                }}
              />
            </div>
          )}
          
          {prevSemesterResultMsg && (
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: prevSemesterResultMsg.includes('Failed') || prevSemesterResultMsg.includes('failed')
                ? 'var(--intent-danger-bg, #fee)'
                : 'var(--intent-success-bg, #efe)',
              color: prevSemesterResultMsg.includes('Failed') || prevSemesterResultMsg.includes('failed')
                ? 'var(--intent-danger-text, #c00)'
                : 'var(--intent-success-text, #060)',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}>
              {prevSemesterResultMsg}
            </div>
          )}
        </>
      )}
    </BaseModal>

    {/* Feedback Modal */}
    <BaseModal
      isOpen={showFeedbackModal}
      onClose={handleCloseFeedbackModal}
      title="Send Feedback"
      maxWidth="600px"
      footer={
        <div className="modal-footer-actions" style={{display: 'flex', gap: 'var(--space-md)', justifyContent: 'flex-end'}}>
          <button 
            className="btn-secondary" 
            onClick={handleCloseFeedbackModal}
            disabled={isSendingFeedback}
          >
            Cancel
          </button>
          <button 
            className="btn-primary" 
            onClick={handleSendFeedback}
            disabled={isSendingFeedback || !feedbackSubject.trim() || !feedbackMessage.trim()}
          >
            {isSendingFeedback ? 'Sending...' : 'Send'}
          </button>
        </div>
      }
    >
      <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)'}}>
        <p style={{
          margin: 0,
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
          lineHeight: 'var(--font-line-height-normal)'
        }}>
          This goes directly to our team at {CORPORATE_EMAIL}
        </p>

        <div>
          <label 
            htmlFor="feedback-subject"
            style={{
              display: 'block',
              marginBottom: 'var(--space-sm)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--color-text-primary)'
            }}
          >
            Subject
          </label>
          <input
            id="feedback-subject"
            type="text"
            value={feedbackSubject}
            onChange={(e) => setFeedbackSubject(e.target.value)}
            maxLength={120}
            placeholder="Brief description of your feedback"
            autoComplete="off"
            style={{
              width: '100%',
              padding: 'var(--space-md)',
              border: '1px solid var(--semantic-input-border)',
              borderRadius: 'var(--semantic-input-radius)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--semantic-input-bg)',
              color: 'var(--semantic-input-text)',
              transition: 'border-color 150ms ease, box-shadow 150ms ease'
            }}
            onFocus={(e) => {
              e.target.style.outline = 'none';
              e.target.style.borderColor = 'var(--semantic-input-border-focus)';
              e.target.style.boxShadow = 'var(--semantic-input-shadow-focus)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--semantic-input-border)';
              e.target.style.boxShadow = 'none';
            }}
          />
          <div style={{
            marginTop: 'var(--space-xs)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            textAlign: 'right'
          }}>
            {feedbackSubject.length}/120
          </div>
        </div>

        <div>
          <label 
            htmlFor="feedback-message"
            style={{
              display: 'block',
              marginBottom: 'var(--space-sm)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--color-text-primary)'
            }}
          >
            Message
          </label>
          <textarea
            id="feedback-message"
            value={feedbackMessage}
            onChange={(e) => setFeedbackMessage(e.target.value)}
            maxLength={4000}
            placeholder="Tell us what's on your mind..."
            rows={8}
            style={{
              width: '100%',
              padding: 'var(--space-md)',
              border: '1px solid var(--semantic-input-border)',
              borderRadius: 'var(--semantic-input-radius)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--semantic-input-bg)',
              color: 'var(--semantic-input-text)',
              resize: 'vertical',
              fontFamily: 'inherit',
              lineHeight: 'var(--font-line-height-normal)',
              transition: 'border-color 150ms ease, box-shadow 150ms ease'
            }}
            onFocus={(e) => {
              e.target.style.outline = 'none';
              e.target.style.borderColor = 'var(--semantic-input-border-focus)';
              e.target.style.boxShadow = 'var(--semantic-input-shadow-focus)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--semantic-input-border)';
              e.target.style.boxShadow = 'none';
            }}
          />
          <div style={{
            marginTop: 'var(--space-xs)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            textAlign: 'right'
          }}>
            {feedbackMessage.length}/4000
          </div>
        </div>
      </div>
    </BaseModal>
    </>
  );
}